/**
 * TanStack Query hooks for the Customer Ops feature.
 *
 * The page is read-only: it loads orders and addresses for a previously
 * selected customer profile. None of the relevant endpoints
 * (`/api/v1/admin/users/search`, `/api/v1/admin/entities/Order`,
 * `/api/v1/admin/entities/Address`) currently expose query-string filters
 * (`customerProfileId`, `profileId`) in the generated OpenAPI types, so
 * we hit them via direct `fetch` with the auth header from the legacy
 * helpers — the same fallback pattern used by `users` and `orders`.
 *
 * TODO: drop the `as never`/direct fetch fallbacks once the backend emits
 * proper query parameters for entity filtering and the user search.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '@favelafood/shared/api/errors';
import { getAccessToken } from '@favelafood/shared/auth/tokens';
import type { components } from '@favelafood/shared/api/generated/api';

type AddressResponse = components['schemas']['AddressResponse'];
type OrderResponse = components['schemas']['OrderResponse'];

/** Minimal user shape returned by `/admin/users/search` (subset of `UserResponse`). */
export interface CustomerHit {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  avatarUrl?: string;
}

interface RawUserHit {
  id?: string;
  fullName?: string;
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
  avatarUrl?: string;
}

interface RawPagedResult<T> {
  content?: T[];
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
}

const baseUrl: string = import.meta.env.VITE_API_BASE ?? '';
const SEARCH_PAGE_SIZE = 20;
const STALE_TIME_MS = 30_000;

/** Centralised query keys to keep cache invalidation predictable. */
export const customerOpsKeys = {
  all: ['customer-ops'] as const,
  search: (query: string) => [...customerOpsKeys.all, 'search', query] as const,
  orders: (profileId: string) => [...customerOpsKeys.all, 'orders', profileId] as const,
  addresses: (profileId: string) => [...customerOpsKeys.all, 'addresses', profileId] as const,
};

/** Builds an `Authorization` header when an access token is available. */
function authHeaders(): HeadersInit {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Issues a GET request and parses the JSON body. Surfaces structured
 * `ApiError`s so callers (TanStack Query) get the same error contract used
 * across the app.
 */
async function getJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const res = await fetch(url, { headers: authHeaders(), signal });
  if (!res.ok) {
    throw new ApiError(res.status, { message: res.statusText }, `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

function mapHit(hit: RawUserHit): CustomerHit | null {
  if (!hit.id) return null;
  const fullName = hit.fullName ?? hit.name ?? hit.id;
  const customer: CustomerHit = {
    id: hit.id,
    fullName,
    email: hit.email ?? '',
  };
  if (hit.phone !== undefined) customer.phone = hit.phone;
  if (hit.avatarUrl !== undefined) customer.avatarUrl = hit.avatarUrl;
  return customer;
}

/**
 * Searches customer users via `/admin/users/search`. Triggered by typing into
 * the customer selector — disabled until at least 2 chars are typed to avoid
 * thrashing the backend with 1-letter requests.
 */
export function useCustomerSearch(query: string): UseQueryResult<CustomerHit[], Error> {
  const trimmed = query.trim();
  return useQuery<CustomerHit[], Error>({
    queryKey: customerOpsKeys.search(trimmed),
    enabled: trimmed.length >= 2,
    staleTime: STALE_TIME_MS,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set('size', String(SEARCH_PAGE_SIZE));
      params.set('q', trimmed);
      params.set('role', 'CUSTOMER');
      const url = `${baseUrl}/api/v1/admin/users/search?${params.toString()}`;
      const raw = await getJson<unknown>(url, signal);
      // Backend may return either `RawUserHit[]` (search endpoint) or a paged
      // envelope. Accept both, prefer the `content` field when present.
      const list: ReadonlyArray<RawUserHit> = Array.isArray(raw)
        ? (raw as RawUserHit[])
        : ((raw as RawPagedResult<RawUserHit>).content ?? []);
      return list.map(mapHit).filter((c): c is CustomerHit => c !== null);
    },
  });
}

/**
 * Loads up to 50 orders for the selected customer profile. The endpoint is
 * the generic admin entity browser (`/admin/entities/Order`), which accepts
 * an arbitrary `customerProfileId` filter the OpenAPI spec doesn't list yet.
 *
 * TODO: switch to a typed `api.GET('/api/v1/admin/entities/Order', { params: ... })`
 * once the spec exposes the `customerProfileId` query param.
 */
export function useCustomerOrders(
  profileId: string | null,
): UseQueryResult<OrderResponse[], Error> {
  return useQuery<OrderResponse[], Error>({
    queryKey: customerOpsKeys.orders(profileId ?? ''),
    enabled: Boolean(profileId),
    staleTime: STALE_TIME_MS,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set('size', '50');
      params.set('sort', 'createdAt');
      params.set('dir', 'desc');
      params.set('customerProfileId', profileId ?? '');
      const url = `${baseUrl}/api/v1/admin/entities/Order?${params.toString()}`;
      const raw = await getJson<RawPagedResult<OrderResponse>>(url, signal);
      return raw.content ?? [];
    },
  });
}

/**
 * Loads up to 50 addresses tied to the selected customer profile. Uses the
 * same generic entity endpoint with a `profileId` filter.
 */
export function useCustomerAddresses(
  profileId: string | null,
): UseQueryResult<AddressResponse[], Error> {
  return useQuery<AddressResponse[], Error>({
    queryKey: customerOpsKeys.addresses(profileId ?? ''),
    enabled: Boolean(profileId),
    staleTime: STALE_TIME_MS,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set('size', '50');
      params.set('profileId', profileId ?? '');
      const url = `${baseUrl}/api/v1/admin/entities/Address?${params.toString()}`;
      const raw = await getJson<RawPagedResult<AddressResponse>>(url, signal);
      return raw.content ?? [];
    },
  });
}
